// import './App.css';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';

import Round from './Round.js';

const ROUND_CONFETTI_DURATION_MS = 1400;

function Game ({ names, savedGame, onPick, onRoundStart, onMatchChange, startNewGame }) {

  const [roundNames, setRoundNames] = useState([]);

  const [currentRound, setCurrentRound] = useState(0);

  const [currentRoundNamesIndex, setCurrentRoundNamesIndex] = useState(0);

  const [isCelebratingRoundWin, setIsCelebratingRoundWin] = useState(false);

  const hasInitialized = useRef(false);
  const roundTransitionTimeoutRef = useRef(null);

  const namesByName = useMemo(() => {
    return names.reduce((carry, entry) => {
      carry[entry.name] = entry;
      return carry;
    }, {});
  }, [names]);

  const hydrateRoundNames = useCallback((savedNames) => {
    return savedNames
      .map((savedName) => namesByName[savedName])
      .filter(Boolean);
  }, [namesByName]);

  const round = useMemo(() => {
    if (roundNames.length < currentRoundNamesIndex + 2) {
      return [];
    }

    return roundNames.slice(currentRoundNamesIndex, currentRoundNamesIndex + 2);
  }, [roundNames, currentRoundNamesIndex]);

  useEffect(() => {
    return () => {
      if (roundTransitionTimeoutRef.current) {
        clearTimeout(roundTransitionTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (hasInitialized.current) {
      return;
    }

    if (savedGame && savedGame.roundNames && savedGame.roundNames.length >= 2) {
      setCurrentRound(savedGame.currentRound || 0);
      setCurrentRoundNamesIndex(savedGame.currentRoundNamesIndex || 0);
      setRoundNames(hydrateRoundNames(savedGame.roundNames));
      hasInitialized.current = true;
      return;
    }

    const nextGame = onRoundStart({ currentRound: 0, currentRoundNamesIndex: 0 });

    if (!nextGame) {
      startNewGame();
      return;
    }

    setCurrentRound(nextGame.currentRound || 0);
    setCurrentRoundNamesIndex(nextGame.currentRoundNamesIndex || 0);
    setRoundNames(hydrateRoundNames(nextGame.roundNames));
    hasInitialized.current = true;
  }, [savedGame, onRoundStart, startNewGame, hydrateRoundNames]);

  const handlePick = (event) => {
    if (isCelebratingRoundWin) {
      return;
    }

    event.preventDefault();
    const selectedValue = event.currentTarget.value;
    const isSkip = selectedValue === '' || selectedValue === 'null';

    let data = round.reduce(function(accumulator, current) {
      let choice = {
        ...current
      };

      // choice.views++;

      let picked = !isSkip && current.name === selectedValue;

      if (picked) {
        choice.votes++;
      } else {
        choice.views += isSkip ? 2 : 1;
      }

      accumulator.push(choice);

      return accumulator;

    }, []);

    onPick({ data });

    const nextRoundNamesIndex = currentRoundNamesIndex + 2;

    if (roundNames.length >= nextRoundNamesIndex + 2) {
      setCurrentRoundNamesIndex(nextRoundNamesIndex);
      onMatchChange({
        roundNames: roundNames.map((entry) => entry.name),
        currentRound: currentRound,
        currentRoundNamesIndex: nextRoundNamesIndex
      });
      return;
    }

    const nextGame = onRoundStart({ currentRound: currentRound + 1, currentRoundNamesIndex: 0 });

    if (!nextGame) {
      startNewGame();
      return;
    }

    const nextRoundNames = hydrateRoundNames(nextGame.roundNames);

    setIsCelebratingRoundWin(true);

    if (roundTransitionTimeoutRef.current) {
      clearTimeout(roundTransitionTimeoutRef.current);
    }

    roundTransitionTimeoutRef.current = setTimeout(() => {
      setCurrentRound(nextGame.currentRound || 0);
      setCurrentRoundNamesIndex(nextGame.currentRoundNamesIndex || 0);
      setRoundNames(nextRoundNames);
      setIsCelebratingRoundWin(false);
      roundTransitionTimeoutRef.current = null;
    }, ROUND_CONFETTI_DURATION_MS);
  };

  return (
    <Round
      round={round}
      onPick={handlePick}
      isCelebrating={isCelebratingRoundWin}
      currentRound={currentRound}
      currentRoundNamesIndex={currentRoundNamesIndex}
      totalRoundNameCount={roundNames.length}
      names={names}
    />
  );
}

export default Game;
